import { useToast } from "@codat/orchard-ui";
import { useCreateCompanyQuery, useSourceTypesQuery } from "api";
import { useCompanyLimitReachedQuery } from "api/client";
import { ConfigResponse } from "api/shared";
import useQueryParams from "hooks/useQueryParams";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import {
  formatUrl,
  getPrioritySourceType,
  getSourceTypeLinkUrl,
  getTryCatchErrorMessage,
  usePageViewedEvent,
} from "utils";

import ErrorFallback from "components/ErrorFallback";
import Landing from "components/Landing";
import Loading from "components/Loading";
import { ClientParams, routes } from "config/urls";
import LimitReached from "pages/LimitReached";

interface ClientLandingContainerProps extends ClientParams {
  data: ConfigResponse;
}

export default function ClientLandingContainer({
  data: config,
  clientId,
}: ClientLandingContainerProps) {
  const { addToast } = useToast();
  const [navigateNext, setNavigateNext] = useState("");
  const queryParams = useQueryParams();
  const locationSearch = useLocation().search;
  const companyName = queryParams.get("companyName");

  const createdByUserId = queryParams.get("user");

  const [{ loading: isLoading }, executePost] = useCreateCompanyQuery({
    clientId,
  });

  const [{ loading: isSourceTypesLoading }, executeGetSourceTypes] =
    useSourceTypesQuery();

  const [{ data: companyLimitReached, loading: isCompanyLimitReachedLoading }] =
    useCompanyLimitReachedQuery({ clientId });

  const isComplete = useMemo(
    () => !isLoading && !isSourceTypesLoading,
    [isLoading, isSourceTypesLoading]
  );

  const goNext = useCallback(async () => {
    if (!companyName) {
      setNavigateNext(
        formatUrl<ClientParams>(routes.client.createCompany, {
          clientId,
        })
      );
      return;
    }

    try {
      const res = await executePost({
        data: {
          name: companyName,
          createdByUserId: createdByUserId,
        },
      });
      const sourceTypes = await executeGetSourceTypes({
        companyId: res.data.id,
      });
      const currentSourceType = getPrioritySourceType(sourceTypes.data);
      setNavigateNext(getSourceTypeLinkUrl(res.data.id, currentSourceType));
    } catch (e) {
      addToast({
        message: `${getTryCatchErrorMessage(e)}: Failed to create company`,
        variant: "error",
      });
    }
  }, [
    clientId,
    companyName,
    createdByUserId,
    executePost,
    addToast,
    executeGetSourceTypes,
  ]);

  const handleClickNext = () => {
    goNext();
  };

  usePageViewedEvent({
    ready: config.showLandingPage,
  });

  useEffect(() => {
    if (companyLimitReached || isCompanyLimitReachedLoading) {
      return;
    }
    if (!config.showLandingPage) {
      goNext();
    }
  }, [
    goNext,
    companyLimitReached,
    isCompanyLimitReachedLoading,
    config.showLandingPage,
  ]);

  if (config.disableLink) {
    return (
      <ErrorFallback
        customStatusCode={400}
        customError="Link has been disabled"
      />
    );
  }

  if (isCompanyLimitReachedLoading) {
    return <Loading dataTestId="company-limit-reached-loading" />;
  }

  if (companyLimitReached) {
    return <LimitReached />;
  }

  if (navigateNext) {
    return (
      <Redirect
        to={{
          pathname: navigateNext,
          search: locationSearch,
        }}
        push={true}
      />
    );
  }
  if (config.showLandingPage) {
    return (
      <Landing
        data={config}
        onClickNext={handleClickNext}
        isLoading={!isComplete}
      />
    );
  }
  return null;
}
